import { memo, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@divlab/divanui';

import * as ModalWindows from '@Stores/ModalWindows';
import validationSchema from './schema';
import useTranslation from '@Queries/useTranslation';
import { useRequest } from '@Contexts/Request';
import FormItem from '@UI/FormItem';
import Button from '@UI/Button';
import Form from '@UI/Form';
import InputPlaceholder from '@UI/InputPlaceholder';
import { useDeps } from '@Contexts/DI';
import styles from './СlarificationProfileForm.module.css';

import type { ChangeEvent, FC } from 'react';
import type { ApiResponse } from '@Types/Api';
import type { ProfileData } from '@Types/Profile';
import type { FormProps } from '@UI/Form';
import type { FormData } from '../../Form';

interface ClarificationProfileFormProps extends Omit<FormProps, 'children'> {
  defaultData: FormData;
  onChangeData?(e: ChangeEvent<HTMLInputElement>): void;
  onSuccess?(): void;
}

type ProfileChangedResponse = {
  status: 'changed';
  profile: ProfileData;
};

type ProfileNotChangedResponse = {
  status: 'not-changed';
};

type ClarificationResponse = ProfileChangedResponse | ProfileNotChangedResponse;

const ClarificationProfileForm: FC<ClarificationProfileFormProps> = (props) => {
  const { className, defaultData, onChangeData, onSuccess, ...restProps } = props;
  const { firstName, email } = defaultData;
  const { t } = useTranslation();
  const { logger } = useDeps();
  const [waiting, setWaiting] = useState(false);
  const { country } = useRequest();
  const queryClient = useQueryClient();
  const [disabledButton, setDisabledButton] = useState(false);

  const handleChangeField = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChangeData) onChangeData(event);
  };

  const handleSubmit = useCallback(() => {
    setWaiting(true);
  }, []);

  const handleResponse = useCallback(
    (res: ApiResponse<ClarificationResponse>) => {
      setWaiting(false);

      if (res.ok === true) {
        if (res.data.status === 'changed') {
          queryClient.invalidateQueries({ queryKey: ['profile'] });
        }

        if (onSuccess) onSuccess();

        return;
      }

      logger.log(res.errors);
    },
    [logger, onSuccess, queryClient],
  );

  const handleError = (err: unknown) => {
    logger.log(err);

    setDisabledButton(true);
    setWaiting(false);

    ModalWindows.open('Info', { view: 'error', variant: 'unknown-error' });
  };

  return (
    <Form
      {...restProps}
      method='POST'
      className={cn(styles.form, className)}
      action='/cabinet/clarification'
      name='clarification'
      validationSchemaBuilder={validationSchema}
      onSubmit={handleSubmit}
      onResponse={handleResponse}
      onCriticalError={handleError}
    >
      <input type='hidden' name='country' value={country} />

      <div className={styles.fields}>
        <FormItem className={styles.formItem}>
          <InputPlaceholder
            className='custom-input'
            placeholder={t('form.name')}
            name='firstName'
            value={firstName}
            onChange={handleChangeField}
          />
        </FormItem>

        <FormItem className={styles.formItem}>
          <InputPlaceholder
            className='custom-input'
            placeholder={t('form.email')}
            name='email'
            value={email}
            onChange={handleChangeField}
          />
        </FormItem>
      </div>

      <FormItem>
        <Button wide theme='primary' type='submit' waiting={waiting} disabled={disabledButton}>
          {t('form.confirm')}
        </Button>
      </FormItem>
    </Form>
  );
};

export default memo(ClarificationProfileForm);
